import {useDataGrid} from "../../../core/components/data-grid/DataGridContext";
import DataGrid, {RenderableExpandRow} from "../../../core/components/data-grid/DataGrid";
import {isRowEvent} from "../../../core/components/data-grid/rowEvent";
import {useEffect, useRef, useState} from "react";
import {t} from "i18next";
import {Button, Dropdown, Menu, Modal, notification, Typography, Tag, Tooltip} from "antd";
import {PERMISSIONS, useUser} from "../../../core/context/UserContext";
import {v4} from "uuid";
import ApiService from "../../../core/service/api-service";
import {DownOutlined, ExclamationCircleOutlined} from "@ant-design/icons";
import RequestRowMenu from "./RequestRowMenu";
import StudyRequestGridTools from "./StudyRequestGridTools";
import ReportPreview from "../../report/ReportPreview";
import {useModal} from "../../../core/context/ModalContext";
import {dateFormat} from "../../../core/utils/date-utils";
import Colors from "../../../core/style/Colors";
import useFocusListener from "../../../core/components/FocusListener";
import useKeypress from "react-use-keypress";
import {Pages} from "../../../AppRouter";
import {studyTaskListKey, studyTaskTypeKey} from "../../../enums/StudyTaskTypeEnum";

import './row-styles.css'

// Compact multi-line cell renderers — shared by the desktop columns and the
// mobile card so a request fits a narrow area (e.g. the study detail drawer).

// ID column extras: request date + acc (modality), stacked under the menu button.
const renderDateAcc = (r) => (
    <div style={{ lineHeight: 1.4, marginTop: 2 }}>
        <div>{r.requestDate ? dateFormat(r.requestDate) : '*'}</div>
        <div style={{ color: '#888' }}>{r.accessionNumber || '*'} ({r.modalityName || '*'})</div>
    </div>
);

// İstem (Request): name + reader/writer + assigned. Labels are bold. User-info
// fields (reader / assigned) are masked to '*' without STUDY_GRID_USER_INFO.
//
// O: çekimin TÜM okuyanları, en son okuyan başta — backend virgülle birleştirip gönderir
// (bkz. RequestTableItem). Sütun dar (210px) olduğu için liste tek satırda kırpılır ve
// tamamı tooltip'te durur; kırpılmasa çok okumalı çekimde Y: alanını ekrandan itiyordu.
const renderReaders = (value, showUserInfo) => {
    if (!showUserInfo)
        return '*';

    if (!value)
        return '*';

    return <Tooltip title={value}>{value}</Tooltip>;
};

const renderReqCell = (r, showUserInfo) => (
    <div style={{ lineHeight: 1.4 }}>
        <div style={{ fontWeight: 600 }}>{r.requestTypeName || '*'}{r.isHL7 ? ' •' : ''}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <b>O:</b> {renderReaders(r.readUsername, showUserInfo)}
            </span>
            <span style={{ whiteSpace: 'nowrap' }}><b>Y:</b> {r.writeUsername || '*'}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span><b>AD:</b> {showUserInfo ? (r.assignedDoctorName || '*') : '*'}</span>
            <span><b>AR:</b> {showUserInfo ? (r.assignedReporterName || '*') : '*'}</span>
        </div>
    </div>
);

// Rapor: report date · confirm date (or re-assessment) · confirmer.
const renderReportCell = (r, showUserInfo) => {
    let confirm;
    if (r.studyTasks?.length > 0) {
        confirm = (
            <Tooltip title={(
                <div>
                    {r.studyTasks.map((task, idx) => (
                        <div key={idx} style={{ marginBottom: 4 }}>
                            <strong>{t(studyTaskTypeKey(task.taskType))}</strong>
                            {task.description && <div style={{ fontSize: 11, color: '#ccc' }}>{task.description}</div>}
                        </div>
                    ))}
                </div>
            )}>
                <Tag color="orange" style={{ margin: 0 }}>{t(studyTaskListKey(r.studyTasks))}</Tag>
            </Tooltip>
        );
    } else {
        confirm = r.reportConfirmDate ? dateFormat(r.reportConfirmDate) : '*';
    }
    return (
        <div style={{ lineHeight: 1.4 }}>
            <div><b>RT:</b> {r.reportDate ? dateFormat(r.reportDate) : '*'}</div>
            <div><b>OT:</b> {confirm}</div>
            {showUserInfo && <div><b>OK:</b> {r.reportConfirmUser || '*'}</div>}
        </div>
    );
};

// Legend for the compact abbreviations used in the cells. Rapor ekranı bunu grid'in üstünde satır olarak
// değil (hideLegend), kart başlığındaki ⓘ tooltip'inde alt alta (vertical) gösterir.
export const RequestGridLegend = ({ vertical = false }) => (
    <div style={vertical
        ? { display: 'flex', flexDirection: 'column', gap: 2 }
        : { fontSize: '8pt', color: '#888', marginBottom: 4, display: 'flex', flexWrap: 'wrap', columnGap: 10, rowGap: 2 }}>
        <span><b>O</b>: {t('Reader')}</span>
        <span><b>Y</b>: {t('Writer')}</span>
        <span><b>RT</b>: {t('Report Date')}</span>
        <span><b>OT</b>: {t('Confirmed Date')}</span>
        <span><b>OK</b>: {t('Confirmed By User')}</span>
        <span><b>AD</b>: {t('Assigned Doctor')}</span>
        <span><b>AR</b>: {t('Assigned Reporter')}</span>
    </div>
);

export default function StudyRequestGrid({studyId, onSelected, name, updateKey, selectedRequestId, listOther, hideReqRepPage, hideLegend = false})
{
    const {hasPermission} = useUser();
    const {setData, setPaginationResult, queryModel, setDataGridProps, setRowColor, refreshDataGrid, setSelectedRows, tableId, selectedRows, setLoading} = useDataGrid();
    const {openModal, closeModal} = useModal()
    const selectedRequest = useRef({id: selectedRequestId})
    const selectedReq = useRef(null)
    // Seçili satır CSS class'ı ile yönetilir (DOM boyama yok). Seçim değişince re-render.
    const [, forceRender] = useState(0)

    const openReportPreviewModal = (studyId, requestId, reportId) => {
        if(!reportId)
        {
            notification.error({
                message: t('The report for this record is not available.')
            })
            return
        }

        openModal(t('Report Preview'), (
            <ReportPreview
                studyId={studyId}
                requestId={requestId}
                reportId={reportId}
                onConfirmed={async () => {
                    await refreshDataGrid();
                }}
                // Önizlemedeki "Kopyala & HBYS" de rapor kaydını değiştiriyor (bkz. ReportRowMenu).
                onDataChanged={() => {
                    refreshDataGrid();
                }}
            />
        ))
    }

    useEffect(() => {
        const showUserInfo = hasPermission(PERMISSIONS.STUDY_GRID_USER_INFO);

        // Compact 5-column layout so a request fits a narrow area (drawer). Each
        // column stacks several fields; see the render helpers above.
        const columns = [
            {
                title: t('Id'),
                key: 'id',
                columnName: 'requestDate',
                width: 110,
                render: (_, record) => (
                    <div style={{ lineHeight: 1.4 }}>
                        <RequestRowMenu row={record} studyId={record.studyId} hideReqRepPage={hideReqRepPage}/>
                        {renderDateAcc(record)}
                    </div>
                )
            },
            {
                title: t('Request'),
                key: 'request',
                className: 'request-req-col',
                width: 218,
                render: (_, record) => renderReqCell(record, showUserInfo)
            },
            {
                title: t('Report'),
                key: 'report',
                columnName: 'reportDate',
                // Ayraç + sol boşluk (row-styles.css): İstem hücresinin sağa yaslı Y:/AR: alanları yapışmasın.
                className: 'request-report-col',
                width: 140,
                render: (_, record) => renderReportCell(record, showUserInfo)
            },
        ];

        setDataGridProps({
            rowKey: 'id',
            columns: columns,
            antdTableProps: {
                scroll: {x: 508},
                pagination: false
            },
            enableRowSelection: true,
            rowClassName: (record, index) => {
                let className = 'normal-row';

                if (record.isRequestDeleted)
                    className = 'deleted-row';
                else if (record.reportIsConfirm)
                    className = 'confirmed-report-row';
                else if (!record.reportIsConfirm)
                    className = 'not-confirmed-report-row'
                else
                    className = 'normal-row'

                if (selectedRequest.current?.id === record.id)
                    className += ' selected-row';

                return className;
            },
            onRow: (record) => {
                return {
                    // isRowEvent: satır menüsünden açılan modal/dropdown gövdeye portal edilse de
                    // React olayları satıra ulaşıyor; olay satırdan gelmiyorsa yok say (bkz. rowEvent.js).
                    onClick: (event) => {
                        if (!isRowEvent(event))
                            return;

                        if(selectedRequest.current?.id === record?.id) {
                            setSelectedReq(null, 'A3')
                        }
                        else {
                            setSelectedReq(record, 'A3')
                        }
                        forceRender(x => x + 1);
                    },
                    onDoubleClick: (event) => {
                        if (!isRowEvent(event))
                            return;

                        setSelectedReq(record, 'A3')
                        forceRender(x => x + 1);
                        hasPermission(PERMISSIONS.REPORT_LIST) && openReportPreviewModal(record.studyId, record.id, record.reportId)
                    }
                }
            }
        })
    }, [])

    // Yükleme göstergesi TABLONUN İÇİNDE (silent: küresel gösterge susar) — açılan satırdaki
    // grid'i tazelemek tüm sayfayı karartmasın.
    const getStudyRequests = async () => {
        setLoading(true)
        let response = {}
        try {
            if(!listOther) {
                response = await ApiService.Request.GetStudyRequestTableList(studyId, {silent: true});
            } else {
                response = await ApiService.Request.GetOtherRequestTableList(studyId, {silent: true});
            }
        }
        finally {
            setLoading(false)
        }
        if(response.success)
        {
            setData(response.data);

            if(response.data.length > 0 && !selectedRequest.current?.id) {
                const idx = response.data.findIndex(x => !x.reportIsConfirm)
                if(idx > -1) {
                    selectedRequest.current = response.data[idx]
                    setSelectedReq(response.data[idx], 'A1')
                    forceRender(x => x + 1)
                }
            }
            else {
                const idx = response.data.findIndex(x => x.id === parseInt(selectedRequest.current.id))
                if(idx > -1) {
                    setSelectedReq(response.data[idx], 'A2')
                    forceRender(x => x + 1)
                }
            }

            // Bring the auto-selected request into view within the (drawer) scroll.
            const selId = selectedRequest.current?.id;
            if (selId) {
                setTimeout(() => {
                    const el = document.querySelector(`[id="${tableId}"] tr[data-row-key="${selId}"]`);
                    el?.scrollIntoView({ block: 'nearest' });
                }, 150);
            }
        }
    }

    const setSelectedReq = (req, type) => {
        onSelected && onSelected(req, type)
        selectedRequest.current = req
    }

    const generateAiReport = async () => {
        const response = await ApiService.ReportAiTemplate.GenerateAiReport(studyId);
        if (response.success) {
            notification.success({ message: t('AI report created successfully.') })
            refreshDataGrid()
        }
    }

    // updateKey (kaydetme sonrası tazeleme) buradaki bağımlılıkta; ayrıca bir [updateKey] effect'i
    // her kayıttan sonra GetStudyRequestTableList'i iki kez gönderiyordu.
    useEffect(() => {
        getStudyRequests()
    }, [queryModel.updateKey, updateKey])

    return (
        <div style={{fontSize: '9pt'}}>
            {
                listOther && (
                    <Typography.Text>
                        {t('OTHER_REQUEST_TEXT')}
                    </Typography.Text>
                )
            }
            {!hideLegend && <RequestGridLegend />}
            <DataGrid
                name={name || 'study-request'}
                mobileCardTitle={(record) => {
                    const showUserInfo = hasPermission(PERMISSIONS.STUDY_GRID_USER_INFO);
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, flex: 1 }}>
                            <RequestRowMenu row={record} studyId={record.studyId} hideReqRepPage={hideReqRepPage}/>
                            {renderDateAcc(record)}
                            {renderReqCell(record, showUserInfo)}
                            {renderReportCell(record, showUserInfo)}
                        </div>
                    );
                }}
                // flex: 1 + minWidth: 0 → araçlar grid başlığında kalan genişliği alıp kendi içinde sarılır;
                // yoksa dar kartta her öğe ayrı satıra düşüp liste/yenile düğmeleri ortada asılı kalıyordu.
                title={() => !listOther && (
                    <div style={{display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', flex: 1, minWidth: 0}}>
                        <StudyRequestGridTools studyId={studyId} />
                        {hasPermission(PERMISSIONS.AI_REPORT_GENERATE) && (
                            <Button
                                size={'small'}
                                type={'default'}
                                onClick={generateAiReport}
                            >
                                {t('AI Report')}
                            </Button>
                        )}
                    </div>
                )}
            />
        </div>
    )
}